import React from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import styles from './DownloadPdfButton.module.scss'
import { Download } from 'lucide-react';
import { connect } from 'react-redux';
import { closeMainModal, openMainModal } from '../../../store/actions';
import { useActionGuard } from '../../../hooks/useActionGuard.ts';
import { fireAnalytics } from '../../../utils/firebase';

const BRAND_COLOR = [35, 75, 162];

const unitToShow = (unit) => {
  const map = {
    m2: "Metro cuadrado",
    m3: "Metro cúbico",
    kg: "Kilogramo",
    ml: "Mililitro",
    cm: "Centímetro",
    u: "Unidad",
    l: "Litro",
    cm2: "Centímetro cuadrado",
    cm3: "Centímetro cúbico",
    gr: "Gramo",
    m: "Metro",
  };
  return map[unit] || unit;
};

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value);
};

const DownloadPDFButton = ({ materials, setShowSendQuotePopup, openMainModal, closeMainModal, previewPopUp, closePreview, userData, className }) => {

  const { guard } = useActionGuard()

  const handleDownloadModal = guard("download_pdf", () => {
    openMainModal({
      title: "Descargar estimación de costos",
      body: "¿Sabías que además de descargar el PDF, puedes enviar la cotización a los proveedores para que te envíen sus ofertas?",
      iconType: "warning",
      dismiss: true,
      yesLabel: "Enviar para cotizar",
      noLabel: "Descargar PDF",
      confirmHandler: () => {
        closeMainModal();
        setShowSendQuotePopup(true)
      },
      customCloseMainModal: () => {
        if (previewPopUp) {
          closePreview();
        }
        closeMainModal();
        fireAnalytics("download_estimacion_de_costos", { user_info_estimacion: userData?.id });
        handleDownload();
      }
    });
  });

  const handleDownload = guard("download_pdf", () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    const formatDate = () => {
      const d = new Date();
      return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    doc.setFontSize(18);
    doc.setTextColor(...BRAND_COLOR);
    doc.text("Estimación de costos", margin, 18);

    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generado el ${formatDate()}`, margin, 26);

    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text("Los precios son orientativos y no representan una oferta formal por parte de Edify.", margin, 33);

    const tableStartY = 40;
    const tableBody = materials?.map((material) => {
      const price = Number(material.price) || 0;
      const quantity = Number(material.quantity) || 0;
      const subtotal = price * quantity;
      return [
        material.name,
        material.category,
        String(quantity),
        unitToShow(material.unit || ""),
        formatCurrency(price),
        formatCurrency(subtotal),
      ];
    });

    const total = materials?.reduce((sum, m) => sum + (Number(m.price) || 0) * (Number(m.quantity) || 1), 0) ?? 0;

    const drawFooter = () => {
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text("Edify — Estimación de costos", margin, pageHeight - 10);
      doc.text(`Pág. ${doc.internal.getNumberOfPages()}`, pageWidth - margin - 10, pageHeight - 10, { align: "right" });
    };

    doc.autoTable({
      head: [["Material", "Categoría", "Cantidad", "Unidad", "Precio Unitario", "Precio Total"]],
      body: tableBody,
      startY: tableStartY,
      theme: "grid",
      margin: { bottom: 20 },
      headStyles: { fillColor: BRAND_COLOR, textColor: [255, 255, 255], fontSize: 10, fontStyle: "bold" },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        2: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      didDrawPage: drawFooter,
    });

    const lastTable = doc.lastAutoTable;
    const summaryY = lastTable ? lastTable.finalY + 10 : tableStartY + 30;

    doc.autoTable({
      body: [["Total", formatCurrency(total)]],
      startY: summaryY,
      theme: "grid",
      margin: { bottom: 20 },
      bodyStyles: { fontSize: 10, fontStyle: "bold" },
      columnStyles: { 0: { halign: "left" }, 1: { halign: "right", textColor: BRAND_COLOR } },
      didDrawPage: drawFooter,
    });

    const fileName = `estimacion-costos-${formatDate().replace(/\//g, "-")}.pdf`;
    doc.save(fileName);
  });

  return (
    <button className={`${styles['download-btn']} ${previewPopUp && styles['preview-btn']} ${className || ''}`.trim()} onClick={guard("download_pdf", handleDownloadModal, () => fireAnalytics("download_estimacion_de_costos", { user_info_estimacion: userData?.id }))} disabled={materials.length === 0}>
      <Download style={{minHeight: "1.6rem", maxHeight: "1.6rem", minWidth: "1.6rem", maxWidth: "1.6rem"}} />
      <span>Descargar estimación</span>
    </button>
  );
};

export default connect(null,{openMainModal, closeMainModal})(DownloadPDFButton);
